import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { ENV } from '@/config/env';
import { StockQuote, StockChartData, StockDataResponse } from '../../types/stock.types';
import { smartCache, getSmartTTL } from '../cache/smartCache';

/**
 * Alpha Vantage API response interfaces
 */
interface AlphaVantageGlobalQuote {
  '01. symbol': string;
  '02. open': string;
  '03. high': string;
  '04. low': string;
  '05. price': string;
  '06. volume': string;
  '07. latest trading day': string;
  '08. previous close': string;
  '09. change': string;
  '10. change percent': string;
}

interface AlphaVantageQuoteResponse {
  'Global Quote': AlphaVantageGlobalQuote;
}

interface AlphaVantageTimeSeriesDaily {
  [date: string]: {
    '1. open': string;
    '2. high': string;
    '3. low': string;
    '4. close': string;
    '5. volume': string;
  };
}

interface AlphaVantageIntradayResponse {
  'Meta Data': {
    '1. Information': string;
    '2. Symbol': string;
    '3. Last Refreshed': string;
    '4. Interval': string;
    '5. Output Size': string;
    '6. Time Zone': string;
  };
  [key: string]: any; // Time Series entries
}

interface AlphaVantageOverview {
  Symbol: string;
  Name: string;
  MarketCapitalization: string;
  PERatio: string;
  [key: string]: string;
}

interface AlphaVantageSymbolMatch {
  '1. symbol': string;
  '2. name': string;
  '3. type': string;
  '4. region': string;
  '5. marketOpen': string;
  '6. marketClose': string;
  '7. timezone': string;
  '8. currency': string;
  '9. matchScore': string;
}

interface AlphaVantageSymbolSearchResponse {
  bestMatches: AlphaVantageSymbolMatch[];
}

export interface SymbolSearchResult {
  symbol: string;
  name: string;
  type: string;
  region: string;
  matchScore: number;
}

/**
 * Stock Data Service using Alpha Vantage API
 * Handles all interactions with Alpha Vantage API for real-time stock data
 *
 * Alpha Vantage Free Tier Limits:
 * - 500 requests per day
 * - 5 requests per minute
 */
class StockDataService {
  private readonly apiUrl: string;
  private readonly apiKey: string;
  private readonly timeout: number = 30000; // 30 seconds
  private requestQueue: number[] = []; // Timestamps of recent requests
  private readonly rateLimit = 5; // 5 requests per minute
  private readonly rateLimitWindow = 60000; // 1 minute in milliseconds

  // Request deduplication: Map of pending requests to prevent duplicate calls
  private pendingRequests: Map<string, Promise<StockDataResponse>> = new Map();

  constructor() {
    this.apiUrl = ENV.ALPHA_VANTAGE_API_URL;
    this.apiKey = ENV.ALPHA_VANTAGE_API_KEY;
  }

  /**
   * Validate API key exists
   */
  private validateApiKey(): void {
    if (!this.apiKey) {
      throw new Error(
        'Alpha Vantage API key is missing. Please set EXPO_PUBLIC_ALPHA_VANTAGE_API_KEY in your environment.'
      );
    }
  }

  /**
   * Validate stock symbol format
   */
  private validateSymbol(symbol: string): boolean {
    const trimmed = symbol.trim();
    return /^[A-Z]{1,5}$/.test(trimmed);
  }

  /**
   * Rate limiting check - Alpha Vantage allows 5 requests per minute
   */
  private async checkRateLimit(): Promise<void> {
    const now = Date.now();

    // Remove requests older than 1 minute
    this.requestQueue = this.requestQueue.filter(time => now - time < this.rateLimitWindow);

    // If we've hit the limit, wait
    if (this.requestQueue.length >= this.rateLimit) {
      const oldestRequest = this.requestQueue[0];
      const waitTime = this.rateLimitWindow - (now - oldestRequest) + 1000; // Add 1 second buffer

      if (__DEV__) {
        console.warn(`⏱️ Rate limit reached. Waiting ${Math.ceil(waitTime / 1000)} seconds...`);
      }

      await this.delay(waitTime);

      // Clean up queue again after waiting
      const newNow = Date.now();
      this.requestQueue = this.requestQueue.filter(time => newNow - time < this.rateLimitWindow);
    }

    // Add current request to queue
    this.requestQueue.push(Date.now());
  }

  /**
   * Delay utility for retry logic
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Generic API request handler with retry logic
   */
  private async apiRequest<T>(
    params: Record<string, string>
  ): Promise<{ data: T | null; error: string | null }> {
    let attempts = 0;
    const maxAttempts = 2;

    while (attempts < maxAttempts) {
      try {
        // Check rate limit before making request
        await this.checkRateLimit();

        const config: AxiosRequestConfig = {
          url: this.apiUrl,
          method: 'GET',
          params: {
            ...params,
            apikey: this.apiKey,
          },
          timeout: this.timeout,
        };

        // Debug: Log the request being made
        if (__DEV__) {
          console.log('📤 Alpha Vantage Request:', {
            function: params.function,
            symbol: params.symbol,
            url: config.url,
          });
        }

        const response = await axios(config);

        // Debug: Log successful response
        if (__DEV__) {
          console.log('✅ Alpha Vantage Response:', {
            status: response.status,
            hasData: !!response.data,
          });
        }

        // Check for Alpha Vantage specific errors
        if (response.data['Error Message']) {
          return { data: null, error: response.data['Error Message'] };
        }

        if (response.data['Note']) {
          // Rate limit message from Alpha Vantage
          return { data: null, error: 'API rate limit reached. Please try again in a moment.' };
        }

        return { data: response.data, error: null };
      } catch (error) {
        const axiosError = error as AxiosError;

        // Detailed error logging for debugging
        if (__DEV__) {
          console.error('🔴 Alpha Vantage API Error:', {
            status: axiosError.response?.status,
            data: axiosError.response?.data,
            message: axiosError.message,
          });
        }

        // Don't retry on certain errors
        if (axiosError.response?.status === 404 || axiosError.response?.status === 400) {
          return { data: null, error: 'Stock symbol not found' };
        }

        if (axiosError.response?.status === 401 || axiosError.response?.status === 403) {
          return { data: null, error: 'Invalid API key. Please check your Alpha Vantage API key.' };
        }

        if (axiosError.code === 'ECONNABORTED' || axiosError.message.includes('timeout')) {
          return { data: null, error: 'Request timed out. Please try again.' };
        }

        if (axiosError.message.includes('Network Error')) {
          return { data: null, error: 'Network error. Check your connection.' };
        }

        // Retry once for other errors
        if (attempts < maxAttempts - 1) {
          await this.delay(2000);
          attempts++;
          continue;
        }

        return { data: null, error: 'Unable to fetch data. Please try again.' };
      }
    }

    return { data: null, error: 'Request failed after retries.' };
  }

  /**
   * Get company overview for additional info
   */
  private async getCompanyOverview(symbol: string): Promise<AlphaVantageOverview | null> {
    try {
      const result = await this.apiRequest<AlphaVantageOverview>({
        function: 'OVERVIEW',
        symbol,
      });

      return result.data;
    } catch (error) {
      console.warn('Failed to fetch company overview:', error);
      return null;
    }
  }

  /**
   * Search for stock symbols by company name or ticker
   * Uses Alpha Vantage SYMBOL_SEARCH endpoint
   *
   * @param keywords - Search query (e.g., "Tesla", "TSLA", "Microsoft")
   * @returns Array of matching symbols with match scores
   */
  async searchSymbol(
    keywords: string
  ): Promise<{ data: SymbolSearchResult[]; error: string | null }> {
    try {
      this.validateApiKey();

      const trimmedKeywords = keywords.trim();

      if (!trimmedKeywords || trimmedKeywords.length < 1) {
        return {
          data: [],
          error: 'Search query cannot be empty',
        };
      }

      const searchResult = await this.apiRequest<AlphaVantageSymbolSearchResponse>({
        function: 'SYMBOL_SEARCH',
        keywords: trimmedKeywords,
      });

      if (searchResult.error || !searchResult.data) {
        return { data: [], error: searchResult.error || 'Symbol search failed' };
      }

      const bestMatches = searchResult.data.bestMatches || [];

      if (bestMatches.length === 0) {
        return {
          data: [],
          error: `No symbols found for "${keywords}"`,
        };
      }

      // Transform to our interface
      const results: SymbolSearchResult[] = bestMatches.map(match => ({
        symbol: match['1. symbol'],
        name: match['2. name'],
        type: match['3. type'],
        region: match['4. region'],
        matchScore: parseFloat(match['9. matchScore']),
      }));

      // Sort by match score (highest first)
      results.sort((a, b) => b.matchScore - a.matchScore);

      if (__DEV__) {
        console.log('🔍 Symbol Search Results:', {
          query: keywords,
          resultsCount: results.length,
          topMatch: results[0],
        });
      }

      return {
        data: results,
        error: null,
      };
    } catch (error) {
      console.error('Symbol search error:', error);

      return {
        data: [],
        error: 'Unable to search symbols. Please try again.',
      };
    }
  }

  /**
   * Get real-time stock quote
   *
   * @param symbol - Stock symbol (e.g., "AAPL", "TSLA")
   * @returns Stock quote data or error
   */
  async getStockQuote(symbol: string): Promise<{ data: StockQuote | null; error: string | null }> {
    try {
      this.validateApiKey();

      const normalizedSymbol = symbol.trim().toUpperCase();

      if (!this.validateSymbol(normalizedSymbol)) {
        return {
          data: null,
          error: 'Invalid stock symbol format',
        };
      }

      const quoteResult = await this.apiRequest<AlphaVantageQuoteResponse>({
        function: 'GLOBAL_QUOTE',
        symbol: normalizedSymbol,
      });

      // DEBUG: Log the actual API response
      console.log(
        '🔍 Raw Alpha Vantage Quote Response:',
        JSON.stringify(quoteResult.data, null, 2)
      );

      if (quoteResult.error || !quoteResult.data) {
        return { data: null, error: quoteResult.error || 'Quote not available' };
      }

      const globalQuote = quoteResult.data['Global Quote'];

      // DEBUG: Log the Global Quote object
      console.log('🔍 Global Quote object:', JSON.stringify(globalQuote, null, 2));

      // Check if quote is valid
      if (!globalQuote || !globalQuote['05. price']) {
        console.log('❌ Quote validation failed:', {
          hasGlobalQuote: !!globalQuote,
          hasPrice: globalQuote ? !!globalQuote['05. price'] : false,
          allKeys: globalQuote ? Object.keys(globalQuote) : [],
        });
        return { data: null, error: 'No quote data available for this symbol' };
      }

      // Fetch company overview for additional info (optional, with error handling)
      // DISABLED: Overview API consumes precious rate limit quota (5 req/min)
      // Only use for detailed company info when absolutely needed
      // console.log('📋 Fetching company overview for', normalizedSymbol);
      // const overview = await this.getCompanyOverview(normalizedSymbol);
      // console.log('📋 Overview fetch result:', !!overview ? 'Success' : 'Failed (using fallback)');
      const overview = null; // Skip overview to save API quota

      const latestPrice = parseFloat(globalQuote['05. price']);
      const change = parseFloat(globalQuote['09. change']);
      const changePercentStr = globalQuote['10. change percent'].replace('%', '');
      const changePercent = parseFloat(changePercentStr);

      const quote: StockQuote = {
        symbol: normalizedSymbol,
        companyName: overview?.Name || normalizedSymbol,
        latestPrice,
        change,
        changePercent,
        high: parseFloat(globalQuote['03. high']),
        low: parseFloat(globalQuote['04. low']),
        open: parseFloat(globalQuote['02. open']),
        previousClose: parseFloat(globalQuote['08. previous close']),
        volume: parseInt(globalQuote['06. volume']),
        marketCap: overview?.MarketCapitalization ? parseFloat(overview.MarketCapitalization) : 0,
        peRatio: overview?.PERatio ? parseFloat(overview.PERatio) : null,
      };

      return {
        data: quote,
        error: null,
      };
    } catch (error) {
      console.error('Stock quote error:', error);

      let errorMessage = 'Unable to fetch stock data. Please try again.';

      if (error instanceof Error) {
        errorMessage = error.message;
      }

      return {
        data: null,
        error: errorMessage,
      };
    }
  }

  /**
   * Get daily chart data (last 30 trading days)
   * Uses TIME_SERIES_DAILY as fallback when intraday isn't available
   *
   * @param symbol - Stock symbol
   * @returns Array of chart data points or error
   */
  async getDailyChart(symbol: string): Promise<{ data: StockChartData[]; error: string | null }> {
    try {
      this.validateApiKey();

      const normalizedSymbol = symbol.trim().toUpperCase();

      if (!this.validateSymbol(normalizedSymbol)) {
        return {
          data: [],
          error: 'Invalid stock symbol format',
        };
      }

      const dailyResult = await this.apiRequest<any>({
        function: 'TIME_SERIES_DAILY',
        symbol: normalizedSymbol,
        outputsize: 'compact', // Last 100 data points
      });

      if (__DEV__) {
        console.log('📅 Daily chart response:', {
          hasError: !!dailyResult.error,
          hasData: !!dailyResult.data,
          dataKeys: dailyResult.data ? Object.keys(dailyResult.data) : [],
        });
      }

      if (dailyResult.error || !dailyResult.data) {
        console.log('❌ Daily chart data error:', dailyResult.error || 'No data returned');
        return { data: [], error: dailyResult.error || 'Chart data not available' };
      }

      const timeSeriesKey = Object.keys(dailyResult.data).find(key =>
        key.startsWith('Time Series')
      );

      if (!timeSeriesKey) {
        console.log(
          '❌ No Time Series key found in daily response. Available keys:',
          Object.keys(dailyResult.data)
        );
        return { data: [], error: 'No chart data available' };
      }

      if (__DEV__) {
        console.log('✅ Found daily time series key:', timeSeriesKey);
      }

      const timeSeries = dailyResult.data[timeSeriesKey];

      // Convert to array and sort by date (most recent last)
      const entries = Object.entries(timeSeries)
        .map(([date, values]: [string, any]) => ({
          date, // Keep Alpha Vantage date format
          open: parseFloat(values['1. open']),
          high: parseFloat(values['2. high']),
          low: parseFloat(values['3. low']),
          close: parseFloat(values['4. close']),
          volume: parseInt(values['5. volume']),
        }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      // Get last 30 data points
      const chartData = entries.slice(-30);

      if (__DEV__) {
        console.log('📊 Daily Chart Data:', {
          totalPoints: entries.length,
          last30Points: chartData.length,
          firstPoint: chartData[0],
          lastPoint: chartData[chartData.length - 1],
        });
      }

      return {
        data: chartData,
        error: null,
      };
    } catch (error) {
      console.error('Daily chart data error:', error);

      return {
        data: [],
        error: 'Unable to fetch chart data. Please try again.',
      };
    }
  }

  /**
   * Get intraday chart data (last 30 data points for today)
   * Uses TIME_SERIES_INTRADAY with 5-minute interval
   * Falls back to daily data if intraday is unavailable
   *
   * @param symbol - Stock symbol
   * @returns Array of chart data points or error
   */
  async getIntradayChart(
    symbol: string
  ): Promise<{ data: StockChartData[]; error: string | null }> {
    try {
      this.validateApiKey();

      const normalizedSymbol = symbol.trim().toUpperCase();

      if (!this.validateSymbol(normalizedSymbol)) {
        return {
          data: [],
          error: 'Invalid stock symbol format',
        };
      }

      const intradayResult = await this.apiRequest<AlphaVantageIntradayResponse>({
        function: 'TIME_SERIES_INTRADAY',
        symbol: normalizedSymbol,
        interval: '5min',
        outputsize: 'compact', // Last 100 data points
      });

      if (__DEV__) {
        console.log('📈 Intraday chart response:', {
          hasError: !!intradayResult.error,
          hasData: !!intradayResult.data,
          dataKeys: intradayResult.data ? Object.keys(intradayResult.data) : [],
        });
      }

      if (intradayResult.error || !intradayResult.data) {
        console.log('⚠️ Intraday data unavailable, falling back to daily data');
        return this.getDailyChart(normalizedSymbol);
      }

      const timeSeriesKey = Object.keys(intradayResult.data).find(key =>
        key.startsWith('Time Series')
      );

      if (!timeSeriesKey) {
        console.log('⚠️ No Time Series key in intraday response, falling back to daily data');
        return this.getDailyChart(normalizedSymbol);
      }

      if (__DEV__) {
        console.log('✅ Found time series key:', timeSeriesKey);
      }

      const timeSeries = intradayResult.data[timeSeriesKey];

      // Convert to array and sort by date (most recent first)
      const entries = Object.entries(timeSeries)
        .map(([date, values]: [string, any]) => ({
          date, // Keep Alpha Vantage date format
          open: parseFloat(values['1. open']),
          high: parseFloat(values['2. high']),
          low: parseFloat(values['3. low']),
          close: parseFloat(values['4. close']),
          volume: parseInt(values['5. volume']),
        }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      // Get last 30 data points
      const chartData = entries.slice(-30);

      if (__DEV__) {
        console.log('📊 Chart Data:', {
          totalPoints: entries.length,
          last30Points: chartData.length,
          firstPoint: chartData[0],
          lastPoint: chartData[chartData.length - 1],
        });
      }

      return {
        data: chartData,
        error: null,
      };
    } catch (error) {
      console.error('Chart data error:', error);

      return {
        data: [],
        error: 'Unable to fetch chart data. Please try again.',
      };
    }
  }

  /**
   * Get complete stock data (quote + chart) with smart caching
   *
   * @param symbol - Stock symbol
   * @param options - Optional: bypassCache to force fresh data
   * @returns Complete stock data response
   */
  async getStockData(
    symbol: string,
    options: { bypassCache?: boolean } = {}
  ): Promise<StockDataResponse> {
    try {
      const normalizedSymbol = symbol.trim().toUpperCase();

      if (!this.validateSymbol(normalizedSymbol)) {
        return {
          quote: null,
          chartData: [],
          error: 'Invalid stock symbol format',
        };
      }

      const cacheKey = `stock_data:${normalizedSymbol}`;

      // Check cache first (unless bypassed)
      if (!options.bypassCache) {
        const cached = await smartCache.get<StockDataResponse>(cacheKey);
        if (cached) {
          const age = await smartCache.getAge(cacheKey);
          console.log(
            `✅ Stock data cache hit for ${normalizedSymbol} (age: ${Math.round((age || 0) / 1000)}s)`,
            {
              hasQuote: !!cached.quote,
              chartDataPoints: cached.chartData?.length || 0,
              hasError: !!cached.error,
            }
          );

          // Pre-fetch in background if cache is stale
          const isStale = await smartCache.isStale(cacheKey);
          if (isStale) {
            console.log(`♻️ Cache stale, refreshing in background for ${normalizedSymbol}`);
            this.refreshStockDataInBackground(normalizedSymbol, cacheKey);
          }

          return cached;
        }
      }

      // DEDUPLICATION: Check if there's already a pending request for this symbol
      const pendingKey = `${normalizedSymbol}:${options.bypassCache ? 'bypass' : 'normal'}`;
      const existingRequest = this.pendingRequests.get(pendingKey);
      if (existingRequest) {
        console.log(`⏳ Waiting for existing request to complete for ${normalizedSymbol}`);
        return existingRequest;
      }

      const callId = `${Date.now()}-${Math.random().toString(36).substring(7)}`;
      console.log(`📡 [${callId}] Fetching fresh stock data for ${normalizedSymbol}`);

      // Create the fetch promise and store it in pending requests
      const fetchPromise = (async () => {
        try {
          // Fetch quote first
          const quoteResult = await this.getStockQuote(normalizedSymbol);

          // If quote fails, return error
          if (quoteResult.error) {
            return {
              quote: null,
              chartData: [],
              error: quoteResult.error,
            };
          }

          // Fetch chart data (non-blocking)
          console.log(`📊 [${callId}] Starting chart data fetch for ${normalizedSymbol}`);
          const chartResult = await this.getIntradayChart(normalizedSymbol);
          console.log(`📊 [${callId}] Chart fetch completed:`, {
            hasData: chartResult.data.length > 0,
            dataPoints: chartResult.data.length,
            hasError: !!chartResult.error,
          });

          // Return quote with chart (even if chart fails)
          const stockData: StockDataResponse = {
            quote: quoteResult.data,
            chartData: chartResult.data,
            error: chartResult.error,
          };

          // Cache the result with smart TTL
          await smartCache.set(cacheKey, stockData, { ttl: getSmartTTL() });

          console.log(`✅ [${callId}] Stock data fetch complete for ${normalizedSymbol}`);

          return stockData;
        } finally {
          // Clean up pending request
          this.pendingRequests.delete(pendingKey);
        }
      })();

      // Store the promise so other callers can await it
      this.pendingRequests.set(pendingKey, fetchPromise);

      return await fetchPromise;
    } catch (error) {
      console.error('Get stock data error:', error);

      return {
        quote: null,
        chartData: [],
        error: 'An unexpected error occurred',
      };
    }
  }

  /**
   * Refresh stock data in background (don't await)
   * Used for stale-while-revalidate pattern
   */
  private async refreshStockDataInBackground(symbol: string, cacheKey: string): Promise<void> {
    try {
      const quoteResult = await this.getStockQuote(symbol);

      if (quoteResult.error || !quoteResult.data) {
        return; // Keep old cache if refresh fails
      }

      const chartResult = await this.getIntradayChart(symbol);

      const stockData: StockDataResponse = {
        quote: quoteResult.data,
        chartData: chartResult.data,
        error: chartResult.error,
      };

      await smartCache.set(cacheKey, stockData, { ttl: getSmartTTL() });
      console.log(`✅ Background refresh completed for ${symbol}`);
    } catch (error) {
      console.error(`Background refresh failed for ${symbol}:`, error);
    }
  }
}

// Export a singleton instance
export const stockDataService = new StockDataService();
